import { Fragment, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ChevronDown, ChevronRight, Download } from 'lucide-react';
import { Card } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { EmptyState } from '../../../components/EmptyState';
import { useAttendanceStore, type DayStatus, type HistoryDay } from '../../../store/attendanceStore';
import {
    DayStatusBadge, LocationStatusBadge, SessionStatusBadge, dayStatusLabel, fmtDate, fmtMinutes, fmtTime, localDateString,
} from './attendanceUi';
import { WeeklySummary } from './WeeklySummary';
import { getExportTimestamp } from '../../../lib/export';

const fieldClass = 'h-9 rounded-lg border border-input bg-background px-3 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring/30';
const shift = (days: number) => { const d = new Date(); d.setDate(d.getDate() + days); return localDateString(d); };
// Canonical order for the Status filter dropdown.
const STATUS_ORDER: DayStatus[] = ['PRESENT', 'HALF_DAY', 'IN_PROGRESS', 'MISSING_CHECKOUT', 'ABSENT', 'ON_LEAVE', 'HOLIDAY', 'WEEK_OFF', 'NOT_CHECKED_IN'];

/** The employee's own attendance, one row per day, sessions on expand. */
export function HistoryPanel() {
    const { history, historyLoading, fetchHistory } = useAttendanceStore();
    const [from, setFrom] = useState(shift(-30));
    const [to, setTo] = useState(localDateString());
    const [open, setOpen] = useState<string | null>(null);
    const [status, setStatus] = useState<DayStatus | 'ALL'>('ALL');

    useEffect(() => { fetchHistory(from, to).catch((e) => toast.error(e.message)); }, [from, to, fetchHistory]);

    const shown = useMemo(
        () => (status === 'ALL' ? history : history.filter((d) => d.summary?.status === status)),
        [history, status]);

    const totals = useMemo(() => {
        const days = shown.filter((d) => d.summary);
        return {
            worked: days.reduce((n, d) => n + (d.summary?.workedMinutes || 0), 0),
            present: days.filter((d) => ['PRESENT', 'HALF_DAY'].includes(d.summary!.status)).length,
            late: days.filter((d) => d.summary?.isLate).length,
            attention: days.filter((d) => d.summary?.hasMissingCheckout || d.summary?.hasLocationException).length,
        };
    }, [shown]);

    const exportCsv = () => {
        const rows = [['Date', 'Status', 'First check-in', 'Last check-out', 'Worked (h)', 'Sessions', 'Late', 'Missing check-out', 'Location exception']];
        for (const d of shown) {
            const s = d.summary;
            rows.push([d.date, s ? dayStatusLabel(s.status) : '', fmtTime(s?.firstCheckIn), fmtTime(s?.lastCheckOut),
                s ? (s.workedMinutes / 60).toFixed(2) : '', String(s?.sessions ?? d.sessions.length), s?.isLate ? 'Yes' : '',
                s?.hasMissingCheckout ? 'Yes' : '', s?.hasLocationException ? 'Yes' : '']);
        }
        const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
        a.download = `attendance_${getExportTimestamp()}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    return (
        <div className="space-y-4">
            <WeeklySummary />

            <Card>
            <div className="flex flex-wrap items-end justify-between gap-3 border-b border-border px-4 py-3">
                <div>
                    <h2 className="font-display text-card-title text-foreground">Attendance history</h2>
                    <p className="text-xs text-muted-foreground">
                        {fmtMinutes(totals.worked)} worked · {totals.present} day(s) present · {totals.late} late · {totals.attention} need attention
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <select className={fieldClass} value={status} onChange={(e) => setStatus(e.target.value as DayStatus | 'ALL')} aria-label="Filter by status">
                        <option value="ALL">All statuses</option>
                        {STATUS_ORDER.map((s) => <option key={s} value={s}>{dayStatusLabel(s)}</option>)}
                    </select>
                    <input className={fieldClass} type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} aria-label="From" />
                    <span className="text-xs text-muted-foreground">to</span>
                    <input className={fieldClass} type="date" value={to} min={from} max={localDateString()} onChange={(e) => setTo(e.target.value)} aria-label="To" />
                    <Button size="sm" variant="secondary" onClick={exportCsv} disabled={!shown.length}><Download /> Export</Button>
                </div>
            </div>
            {shown.length === 0 ? (
                <EmptyState className="m-4 border-0"
                    title={historyLoading ? 'Loading…' : status !== 'ALL' ? `No ${dayStatusLabel(status).toLowerCase()} days in this period` : 'No attendance in this period'}
                    description={status !== 'ALL' ? 'Try a different status or widen the date range.' : 'Days you worked, were absent or on leave appear here.'} />
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="border-b border-border bg-secondary/50 text-left text-xs text-muted-foreground">
                            <tr>
                                <th className="w-8 px-3 py-2" />
                                <th className="px-3 py-2 font-semibold">Date</th>
                                <th className="px-3 py-2 font-semibold">Status</th>
                                <th className="px-3 py-2 font-semibold">First in</th>
                                <th className="px-3 py-2 font-semibold">Last out</th>
                                <th className="px-3 py-2 text-right font-semibold">Worked</th>
                                <th className="px-3 py-2 font-semibold">Notes</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {shown.map((d: HistoryDay) => (
                                <Fragment key={d.date}>
                                    <tr className="cursor-pointer hover:bg-secondary/30" onClick={() => setOpen(open === d.date ? null : d.date)}>
                                        <td className="px-3 py-2 text-muted-foreground">{d.sessions.length ? (open === d.date ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />) : null}</td>
                                        <td className="whitespace-nowrap px-3 py-2 font-medium text-foreground">{fmtDate(d.date)}</td>
                                        <td className="px-3 py-2"><DayStatusBadge status={d.summary?.status} /></td>
                                        <td className="whitespace-nowrap px-3 py-2 tabular-nums text-muted-foreground">{fmtTime(d.summary?.firstCheckIn)}</td>
                                        <td className="whitespace-nowrap px-3 py-2 tabular-nums text-muted-foreground">{fmtTime(d.summary?.lastCheckOut)}</td>
                                        <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-foreground">{d.summary ? fmtMinutes(d.summary.workedMinutes) : '—'}</td>
                                        <td className="px-3 py-2">
                                            <div className="flex flex-wrap gap-1">
                                                {d.summary?.isLate && <Badge variant="warning">Late {fmtMinutes(d.summary.lateMinutes)}</Badge>}
                                                {d.summary?.isEarlyCheckout && <Badge variant="warning">Left early</Badge>}
                                                {d.summary?.hasLocationException && <Badge variant="warning">Location</Badge>}
                                                {d.summary?.adjustmentPending && <Badge variant="info">Adjustment pending</Badge>}
                                            </div>
                                        </td>
                                    </tr>
                                    {open === d.date && d.sessions.map((s) => (
                                        <tr key={s.id} className={`bg-secondary/20 ${s.status === 'SUPERSEDED' ? 'opacity-60' : ''}`}>
                                            <td />
                                            <td className="px-3 py-2 text-xs text-muted-foreground">Session {s.label}</td>
                                            <td className="px-3 py-2"><SessionStatusBadge status={s.status} /></td>
                                            <td className="whitespace-nowrap px-3 py-2 tabular-nums text-xs text-foreground">{fmtTime(s.checkIn)}</td>
                                            <td className="whitespace-nowrap px-3 py-2 tabular-nums text-xs text-foreground">{s.checkOut ? fmtTime(s.checkOut) : '—'}</td>
                                            <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-xs text-foreground">{fmtMinutes(s.creditedMinutes)}</td>
                                            <td className="px-3 py-2 text-xs text-muted-foreground">
                                                <div className="flex flex-wrap items-center gap-1">
                                                    <span>{s.checkInLocation || 'Location not verified'}</span>
                                                    {s.checkInLocationStatus !== 'VALID' && <LocationStatusBadge status={s.checkInLocationStatus} />}
                                                </div>
                                                {s.exception && <p className="mt-0.5">{s.exception}</p>}
                                            </td>
                                        </tr>
                                    ))}
                                </Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            </Card>
        </div>
    );
}
